import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import { breakGlassSignIn } from '@/lib/auth/break-glass-auth';

/**
 * Emergency Login (Spec 0044) — minimale Seite.
 * Nur Passwort-Feld. Kein Email. Kein Link zur normalen Login-Seite.
 * Kein "Passwort vergessen". Nur fuer autorisierte Notfall-Zugriffe.
 */
export function EmergencyLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { refresh } = useAuth();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError(t('emergencyLogin.error.accessDenied'));
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await breakGlassSignIn(password);

      if (result.success) {
        await refresh(); // Auth-State aktualisieren (laed Break-Glass User)
        navigate('/', { replace: true });
        return;
      }

      setError(t('emergencyLogin.error.accessDenied'));
    } catch {
      setError(t('emergencyLogin.error.accessDenied'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-6">
          <div className="mb-6 text-center">
            <h1 className="text-lg font-semibold text-foreground">{t('emergencyLogin.title')}</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('emergencyLogin.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="emergency-password">{t('emergencyLogin.passwordLabel')}</Label>
              <Input
                id="emergency-password"
                type="password"
                autoComplete="off"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  {t('emergencyLogin.submitting')}
                </span>
              ) : (
                t('emergencyLogin.submit')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
